import { itemOptionsQuantitiesAtom, itemsAtom, mandatoryOptionGroupsAtom } from "@/atoms";
import PromotionalRibbon from "@/components/PromotionalRibbon";
import { OptionCard } from "@/components/item";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/cn";
import { isOptionAvailable } from "@/lib/is-option-available";
import { getPortionTarget, isPortionGroupSatisfied, isPortionOptionGroup } from "@/lib/option-group-portions";
import { Item, ItemOptionsQuantities, OptionGroup } from "@/types";
import { ItemOfferDisplayMode } from "@/types/item-offer";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ChangeEvent, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Callout, PageContainer, Heading, SearchField, Text } from "@/components/ui";
import { OptionGroupRules } from "./OptionGroupRules";

type OptionGroupCardProps = {
  optionGroup: OptionGroup;
  step: number;
  everyStep: number;
  displayMode?: ItemOfferDisplayMode;
};

export default function OptionGroupCard({ optionGroup, step, everyStep, displayMode }: OptionGroupCardProps) {
  const itemOptionsMapAtom = useMemo(() => atom<Record<string, Item>>((get) => {
    const items = get(itemsAtom);
    return optionGroup.options.reduce((acc, option) => {
      const item = items.find(i => i.id === option.itemId);
      if (item) {
        acc[option.id] = item;
      }
      return acc;
    }, {} as Record<string, Item>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [])

  const itemOptionsMap = useAtomValue(itemOptionsMapAtom)
  const setMandatoryOptionGroups = useSetAtom(mandatoryOptionGroupsAtom)
  const [itemOptionsQuantities, setItemOptionsQuantities] = useAtom(itemOptionsQuantitiesAtom)
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const [selectedRadioId, setSelectedRadioId] = useState("")
  const [totalQuantity, setTotalQuantity] = useState(0);

  const [filteredItemOptions, setFilteredItemOptions] = useState<Item[]>([])
  const [filterValue, setFilterValue] = useState("")

  const shouldShowSearch = useMemo(() => {
    const optionsCount = optionGroup.options.length;
    return isDesktop ? optionsCount >= 10 : optionsCount >= 5;
  }, [optionGroup.options.length, isDesktop]);

  useEffect(() => {
    const existingGroup = itemOptionsQuantities[optionGroup.id];
    const hasExistingSelections = existingGroup &&
      Object.values(existingGroup).some(v => v > 0);

    if (hasExistingSelections) return;

    const initialItemOptionsQuantities: ItemOptionsQuantities = optionGroup.options
      .reduce((acc, option) => (
        { ...acc, [option.id]: 0 }), {}
      )

    setItemOptionsQuantities(state => ({
      ...state,
      [optionGroup.id]: initialItemOptionsQuantities
    }))

    if (optionGroup.minPermitted > 0 && optionGroup.options.length === 1) {
      const singleOption = optionGroup.options[0];
      if (!isOptionAvailable(singleOption, itemOptionsMap[singleOption.id])) return;
      setSelectedRadioId(singleOption.id);
      setItemOptionsQuantities(state => ({
        ...state,
        [optionGroup.id]: {
          ...state[optionGroup.id],
          [singleOption.id]: optionGroup.minPermitted
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemOptionsMap])

  useLayoutEffect(() => {
    const existingGroup = itemOptionsQuantities[optionGroup.id]

    if (existingGroup) {
      const totalQuantity = Object.values(existingGroup).reduce((acc, value) => acc + value, 0)
      setTotalQuantity(totalQuantity)
      
      if (optionGroup.maxPermitted === 1) {
        const selected = optionGroup.options.find(opt => existingGroup[opt.id] > 0);
        setSelectedRadioId(selected?.id ?? "");
      }
    } else {
      setTotalQuantity(0)
      setSelectedRadioId("")
    }
  }, [itemOptionsQuantities, optionGroup.id, optionGroup.maxPermitted, optionGroup.options])

  useEffect(() => {
    const values = Object.values(itemOptionsMap)

    if (!filterValue.length) {
      setFilteredItemOptions(values)
    } else {
      const filtered = values.filter(i => i.name.toUpperCase().includes(filterValue.toUpperCase()))
      setFilteredItemOptions(filtered)
    }
  }, [filterValue, itemOptionsMap])

  useEffect(() => {
    const currentGroupQty = itemOptionsQuantities[optionGroup.id] ?? {};
    const isPortionGroup = isPortionOptionGroup(optionGroup);
    const portionTarget = getPortionTarget(optionGroup);

    if (isPortionGroup && portionTarget) {
      const isValid = isPortionGroupSatisfied(currentGroupQty, portionTarget, optionGroup.minPermitted);
      setMandatoryOptionGroups(state => ({ ...state, [optionGroup.id]: isValid }))
      return;
    }

    if (totalQuantity >= optionGroup.minPermitted) setMandatoryOptionGroups(state => ({ ...state, [optionGroup.id]: true }))
    else setMandatoryOptionGroups(state => ({ ...state, [optionGroup.id]: false }))
  }, [totalQuantity, itemOptionsQuantities, optionGroup, setMandatoryOptionGroups])

  function filterItemOptions(e: ChangeEvent<HTMLInputElement>) { setFilterValue(e.target.value) }
  function clearFilter() { setFilterValue("") }

  const isDisabled = optionGroup.maxPermitted > 0 && totalQuantity === optionGroup.maxPermitted

  return (
    <div data-testid={`option-group-${optionGroup.id}`}>
      <div className='relative flex justify-between gap-4 overflow-hidden pt-3 pb-2'>
        <PromotionalRibbon
          isPromotional={optionGroup.isPromotional}
          text={optionGroup.promotionalBannerText}
        />
        <PageContainer className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Heading level={2} size="section">
                {optionGroup.name}
              </Heading>
              <OptionGroupRules optionGroup={optionGroup} />
            </div>
            <div className="flex shrink-0 flex-col items-center justify-center">
              <Text size="sm" weight="bold">
                Passo
              </Text>
              <Text size="sm" weight="bold">
                {step}/{everyStep}
              </Text>
            </div>
          </div>
          {shouldShowSearch && (
            <SearchField
              placeholder="Digite para buscar"
              aria-label="Buscar opções"
              value={filterValue}
              onChange={filterItemOptions}
              onClear={clearFilter}
            />
          )}
        </PageContainer>
      </div>
      {(optionGroup.priceMethod === "MAX" && optionGroup.maxPermitted > 1) && (
        <PageContainer className="mt-2">
          <Callout tone="warning" title="Como funciona o preço">
            Esse item é cobrado pelo preço cheio da opção mais cara que você escolher.
          </Callout>
        </PageContainer>
      )}
      <PageContainer className={cn("flex flex-col gap-3 py-3", displayMode === ItemOfferDisplayMode.STEP_BY_STEP && "mb-20")}>
        {filteredItemOptions.map((item) => {
          const option = optionGroup.options.find((op) => op.itemId === item.id);
          return (
            <OptionCard
              key={item.id}
              item={item}
              disabled={isDisabled}
              unavailable={!isOptionAvailable(option, item)}
              optionGroup={optionGroup}
              selectedRadioId={selectedRadioId}
              setSelectedRadioIdAction={setSelectedRadioId}
            />
          );
        })}
      </PageContainer>
    </div>
  );
}
